<template>
  <div class="animated fadeIn dashboard-modern">
    <div class="dashboard-meta">
      <div class="meta-block">
        <span class="meta-label">Stats</span>
        <span class="meta-value">{{ formatAge(statsUpdatedAt) }}</span>
        <span class="meta-help" title="Stats = time when core toplists/aggregates were last refreshed from game DB.">
          <i class="fa fa-info-circle"></i>
        </span>
      </div>
      <div class="meta-block" v-if="rangeLoading">
        <span class="meta-warning"><i class="fa fa-circle-o-notch fa-spin mr-1"></i> Loading range</span>
      </div>
      <div class="meta-block meta-block--range">
        <span class="meta-label">Range</span>
        <div class="range-switch">
          <button
            v-for="range in availableRanges"
            :key="'range-' + range.days"
            type="button"
            class="range-pill"
            :class="{ active: statsRange === range.days }"
            @click="setStatsRange(range.days)"
          >
            {{ range.label }}
          </button>
        </div>
      </div>
      <div class="meta-block" v-if="isLoading">
        <span class="meta-warning"><i class="fa fa-circle-o-notch fa-spin mr-1"></i> Loading</span>
      </div>
    </div>

    <div class="dashboard-content">
      <b-row>
        <b-col lg="6">
          <b-card class="mb-3 status-card status-card--silver" :class="{'is-loading': statusLoading}" no-body>
            <b-card-body>
              <div class="d-flex align-items-start justify-content-between">
                <div>
                  <div class="status-label">Silver</div>
                  <div class="status-value">
                    <span v-if="statusLoading" class="skeleton-text skeleton-text--lg"></span>
                    <span v-else>{{ silverOnline ? formatNumber(silverCount) : 'Offline' }}</span>
                  </div>
                  <div class="text-muted">
                    <span v-if="statusLoading" class="skeleton-text"></span>
                    <span v-else>{{ silverOnline ? 'Players online' : 'Server offline' }}</span>
                  </div>
                </div>
                <b-dropdown v-if="hasPermission(ForceRestart) || hasPermission(PVPStatisticsDL)" class="float-right" variant="transparent p-0" right>
                  <template slot="button-content">
                    <i class="icon-settings"></i>
                  </template>
                  <b-dropdown-header>Server Tools</b-dropdown-header>
                  <b-dropdown-item v-if="hasPermission(ForceRestart)" @click="confirmRestart('silver')">
                    <i class="fa fa-server text-white"></i> Force Restart
                  </b-dropdown-item>
                  <b-dropdown-item v-if="hasPermission(PVPStatisticsDL)" href="/getcsv/ratings/silver">
                    <i class="fa fa-street-view text-white"></i> Download Top 50 PVP Ratings
                  </b-dropdown-item>
                  <b-dropdown-item v-if="hasPermission(PVPStatisticsDL)" href="/getcsv/losses/silver">
                    <i class="fa fa-street-view text-white"></i> Download Top 100 PVP Losses
                  </b-dropdown-item>
                  <b-dropdown-item v-if="hasPermission(PVPStatisticsDL)" href="/getcsv/random_ratings/silver">
                    <i class="fa fa-street-view text-white"></i> Download Top 50 Random Ratings
                  </b-dropdown-item>
                  <b-dropdown-item v-if="hasPermission(PVPStatisticsDL)" href="/getcsv/random_losses/silver">
                    <i class="fa fa-street-view text-white"></i> Download Top 100 Random Losses
                  </b-dropdown-item>
                </b-dropdown>
              </div>
          </b-card-body>
          <div class="status-chart">
            <card-line3-chart ref="silverChart" :data="silverChartData" :showYAxis="true" class="chart-wrapper" style="height:84px;" height="84"/>
            <div v-if="rangeLoading" class="chart-loading-overlay">
              <i class="fa fa-circle-o-notch fa-spin"></i>
            </div>
          </div>
        </b-card>
      </b-col>
        <b-col lg="6">
          <b-card class="mb-3 status-card status-card--gold" :class="{'is-loading': statusLoading}" no-body>
            <b-card-body>
              <div class="d-flex align-items-start justify-content-between">
                <div>
                  <div class="status-label">Gold</div>
                  <div class="status-value">
                    <span v-if="statusLoading" class="skeleton-text skeleton-text--lg"></span>
                    <span v-else>{{ goldOnline ? formatNumber(goldCount) : 'Offline' }}</span>
                  </div>
                  <div class="text-muted">
                    <span v-if="statusLoading" class="skeleton-text"></span>
                    <span v-else>{{ goldOnline ? 'Players online' : 'Server offline' }}</span>
                  </div>
                </div>
                <b-dropdown v-if="hasPermission(ForceRestart) || hasPermission(PVPStatisticsDL)" class="float-right" variant="transparent p-0" right>
                  <template slot="button-content">
                    <i class="icon-settings"></i>
                  </template>
                  <b-dropdown-header>Server Tools</b-dropdown-header>
                  <b-dropdown-item v-if="hasPermission(ForceRestart)" @click="confirmRestart('gold')">
                    <i class="fa fa-server text-white"></i> Force Restart
                  </b-dropdown-item>
                  <b-dropdown-item v-if="hasPermission(PVPStatisticsDL)" href="/getcsv/ratings/gold">
                    <i class="fa fa-street-view text-white"></i> Download Top 50 PVP Ratings
                  </b-dropdown-item>
                  <b-dropdown-item v-if="hasPermission(PVPStatisticsDL)" href="/getcsv/losses/gold">
                    <i class="fa fa-street-view text-white"></i> Download Top 100 PVP Losses
                  </b-dropdown-item>
                  <b-dropdown-item v-if="hasPermission(PVPStatisticsDL)" href="/getcsv/random_ratings/gold">
                    <i class="fa fa-street-view text-white"></i> Download Top 50 Random Ratings
                  </b-dropdown-item>
                  <b-dropdown-item v-if="hasPermission(PVPStatisticsDL)" href="/getcsv/random_losses/gold">
                    <i class="fa fa-street-view text-white"></i> Download Top 100 Random Losses
                  </b-dropdown-item>
                </b-dropdown>
              </div>
          </b-card-body>
          <div class="status-chart">
            <card-line3-chart ref="goldChart" :data="goldChartData" :showYAxis="true" class="chart-wrapper" style="height:84px;" height="84"/>
            <div v-if="rangeLoading" class="chart-loading-overlay">
              <i class="fa fa-circle-o-notch fa-spin"></i>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

      <b-row>
        <b-col xl="4" md="6">
          <b-card class="mb-3 stat-card" :class="{'is-loading': statsLoading}">
            <div class="stat-label">Active players</div>
            <div class="stat-value">
              <span v-if="statsLoading" class="skeleton-text skeleton-text--lg"></span>
              <span v-else>{{ formatNumber(activePlayersTotal) }}</span>
            </div>
            <div class="text-muted small">
              <span v-if="statsLoading" class="skeleton-text"></span>
              <span v-else>Silver {{ formatNumber(activePlayersSilver) }} · Gold {{ formatNumber(activePlayersGold) }}</span>
            </div>
            <div class="mini-chart">
              <card-line3-chart :data="activePlayersChartData" class="chart-wrapper" style="height:72px;" height="72"/>
              <div v-if="rangeLoading" class="chart-loading-overlay">
                <i class="fa fa-circle-o-notch fa-spin"></i>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col xl="4" md="6">
          <b-card class="mb-3 stat-card" :class="{'is-loading': statsLoading}">
            <div class="stat-label">New players</div>
            <div class="stat-value">
              <span v-if="statsLoading" class="skeleton-text skeleton-text--lg"></span>
              <span v-else>{{ formatNumber(newPlayersTotal) }}</span>
            </div>
            <div class="text-muted small">
              <span v-if="statsLoading" class="skeleton-text"></span>
              <span v-else>All servers</span>
            </div>
            <div class="mini-chart">
              <card-line3-chart :data="newPlayersChartData" class="chart-wrapper" style="height:72px;" height="72"/>
              <div v-if="rangeLoading" class="chart-loading-overlay">
                <i class="fa fa-circle-o-notch fa-spin"></i>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col xl="4" md="12">
          <b-card class="mb-3 stat-card" :class="{'is-loading': statsLoading}">
            <div class="stat-label">Average money</div>
            <div class="stat-value">
              <span v-if="statsLoading" class="skeleton-text skeleton-text--lg"></span>
              <span v-else>${{ formatNumber(averageMoneyTotal) }}</span>
            </div>
            <div class="text-muted small">
              <span v-if="statsLoading" class="skeleton-text"></span>
              <span v-else>Silver ${{ formatNumber(averageMoneySilver) }} · Gold ${{ formatNumber(averageMoneyGold) }}</span>
            </div>
            <div class="mini-chart">
              <card-line3-chart :data="averageMoneyChartData" class="chart-wrapper" style="height:72px;" height="72"/>
              <div v-if="rangeLoading" class="chart-loading-overlay">
                <i class="fa fa-circle-o-notch fa-spin"></i>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col xl="4" lg="6">
          <b-card class="mb-3 list-card">
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <div><i class="fa fa-money"></i> <strong>Top Money</strong></div>
                <servermenu v-model="activeMoney"></servermenu>
              </div>
            </template>
            <b-list-group flush>
              <template v-if="stats">
                <b-list-group-item
                  v-for="item in filteredToplist(stats.money_toplist, activeMoney)"
                  :key="item.server + '_' + item.username"
                  :class="['toplist-row', serverAccentClass(item.server)]">
                  <img :src="'https://pokemonrevolution.net/forum/avatar.php?user=' + item.username + '&background=' + servers[item.server].hex.substr(1)" alt="" class="img-avatar mr-3"/>
                  <div class="flex-grow-1">
                    <div class="toplist-name">
                      <router-link :to="'/user/' + item.username + '/profile'">{{ item.username }}</router-link>
                    </div>
                    <div class="toplist-meta text-muted">{{ Math.floor(item.playtime / 60) }}h Playtime</div>
                  </div>
                  <div class="toplist-value">
                    <span class="value-label text-muted">Money</span>
                    <b-badge variant="dark" class="border border-secondary value-badge">${{ formatNumber(item.money) }}</b-badge>
                  </div>
                </b-list-group-item>
              </template>
              <template v-else>
                <b-list-group-item v-for="n in 5" :key="'money-skel-' + n" class="toplist-row">
                  <div class="img-avatar mr-3 skeleton-circle"></div>
                  <div class="flex-grow-1">
                    <div class="skeleton-text skeleton-text--lg"></div>
                    <div class="skeleton-text skeleton-text--sm"></div>
                  </div>
                  <div class="toplist-value">
                    <div class="skeleton-text skeleton-text--sm"></div>
                  </div>
                </b-list-group-item>
              </template>
            </b-list-group>
          </b-card>
        </b-col>
        <b-col xl="4" lg="6">
          <b-card class="mb-3 list-card">
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <div><i class="fa fa-trophy"></i> <strong>Ranked Wins</strong></div>
                <servermenu v-model="activeWins"></servermenu>
              </div>
            </template>
            <b-list-group flush>
              <template v-if="stats">
                <b-list-group-item
                  v-for="item in filteredToplist(stats.wins_toplist, activeWins)"
                  :key="item.server + '_' + item.username"
                  :class="['toplist-row', serverAccentClass(item.server)]">
                  <img :src="'https://pokemonrevolution.net/forum/avatar.php?user=' + item.username + '&background=' + servers[item.server].hex.substr(1)" alt="" class="img-avatar mr-3"/>
                  <div class="flex-grow-1">
                    <div class="toplist-name">
                      <router-link :to="'/user/' + item.username + '/profile'">{{ item.username }}</router-link>
                    </div>
                    <div class="toplist-meta text-muted">{{ item.losses }} Losses</div>
                  </div>
                  <div class="toplist-value">
                    <span class="value-label text-muted">Wins</span>
                    <b-badge variant="dark" class="border border-secondary value-badge">{{ formatNumber(item.wins) }}</b-badge>
                  </div>
                </b-list-group-item>
              </template>
              <template v-else>
                <b-list-group-item v-for="n in 5" :key="'wins-skel-' + n" class="toplist-row">
                  <div class="img-avatar mr-3 skeleton-circle"></div>
                  <div class="flex-grow-1">
                    <div class="skeleton-text skeleton-text--lg"></div>
                    <div class="skeleton-text skeleton-text--sm"></div>
                  </div>
                  <div class="toplist-value">
                    <div class="skeleton-text skeleton-text--sm"></div>
                  </div>
                </b-list-group-item>
              </template>
            </b-list-group>
          </b-card>
        </b-col>
        <b-col xl="4" lg="12">
          <b-card class="mb-3 list-card">
            <template #header>
              <div class="d-flex justify-content-between align-items-center">
                <div><i class="fa fa-times-circle"></i> <strong>Ranked Losses</strong></div>
                <servermenu v-model="activeLosses"></servermenu>
              </div>
            </template>
            <b-list-group flush>
              <template v-if="stats">
                <b-list-group-item
                  v-for="item in filteredToplist(stats.losses_toplist, activeLosses)"
                  :key="item.server + '_' + item.username"
                  :class="['toplist-row', serverAccentClass(item.server)]">
                  <img :src="'https://pokemonrevolution.net/forum/avatar.php?user=' + item.username + '&background=' + servers[item.server].hex.substr(1)" alt="" class="img-avatar mr-3"/>
                  <div class="flex-grow-1">
                    <div class="toplist-name">
                      <router-link :to="'/user/' + item.username + '/profile'">{{ item.username }}</router-link>
                    </div>
                    <div class="toplist-meta text-muted">{{ item.wins }} Wins</div>
                  </div>
                  <div class="toplist-value">
                    <span class="value-label text-muted">Losses</span>
                    <b-badge variant="dark" class="border border-secondary value-badge">{{ formatNumber(item.losses) }}</b-badge>
                  </div>
                </b-list-group-item>
              </template>
              <template v-else>
                <b-list-group-item v-for="n in 5" :key="'losses-skel-' + n" class="toplist-row">
                  <div class="img-avatar mr-3 skeleton-circle"></div>
                  <div class="flex-grow-1">
                    <div class="skeleton-text skeleton-text--lg"></div>
                    <div class="skeleton-text skeleton-text--sm"></div>
                  </div>
                  <div class="toplist-value">
                    <div class="skeleton-text skeleton-text--sm"></div>
                  </div>
                </b-list-group-item>
              </template>
            </b-list-group>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import Servermenu from './dashboard/Servermenu'
  import Servers from '../_servers'
  import CardLine3Chart from './dashboard/CardLine3Chart'
  import VueNotifications from 'vue-notifications'
  import iziToast from 'izitoast'
  export default {
    name: 'dashboard',
    components: {
      Servermenu,
      CardLine3Chart
    },
    data: function () {
      return {
        activeMoney: null,
        activeWins: null,
        servers: Servers,
        activeLosses: null,
        refreshIntervalId: null,
        statsRange: 14,
        lastOnlineHistorySig: '',
        lastStatsSig: '',
        rangeLoading: false,
        silverChartData: {
          labels: [],
          datasets: [{
            label: 'online',
            backgroundColor: 'rgba(0,0,0,0)',
            borderColor: 'rgba(203, 211, 219, 0.9)',
            data: [],
            fill: false
          }]
        },
        goldChartData: {
          labels: [],
          datasets: [{
            label: 'online',
            backgroundColor: 'rgba(0,0,0,0)',
            borderColor: 'rgba(230, 199, 146, 0.9)',
            data: [],
            fill: false
          }]
        },
        activePlayersChart: {
          labels: [],
          datasets: [{
            label: 'trend',
            backgroundColor: 'rgba(0,0,0,0)',
            borderColor: 'rgba(0, 150, 136, 0.7)',
            data: [],
            fill: false,
            borderWidth: 2,
            lineTension: 0.35,
            pointRadius: 0,
            pointHitRadius: 6
          }]
        },
        newPlayersChart: {
          labels: [],
          datasets: [{
            label: 'trend',
            backgroundColor: 'rgba(0,0,0,0)',
            borderColor: 'rgba(0, 150, 136, 0.7)',
            data: [],
            fill: false,
            borderWidth: 2,
            lineTension: 0.35,
            pointRadius: 0,
            pointHitRadius: 6
          }]
        },
        averageMoneyChart: {
          labels: [],
          datasets: [{
            label: 'trend',
            backgroundColor: 'rgba(0,0,0,0)',
            borderColor: 'rgba(0, 150, 136, 0.7)',
            data: [],
            fill: false,
            borderWidth: 2,
            lineTension: 0.35,
            pointRadius: 0,
            pointHitRadius: 6
          }]
        }
      }
    },
    created () {
      this.loadFilters()
      this.loadStatsRange()
      this.emit('dashboard', { days: this.statsRange })
      this.refreshOnlineCharts()
    },
    mounted () {
      this.startAutoRefresh()
    },
    beforeDestroy () {
      if (this.refreshIntervalId) {
        clearInterval(this.refreshIntervalId)
        this.refreshIntervalId = null
      }
    },
    methods: {
      startAutoRefresh () {
        if (this.refreshIntervalId) return
        this.refreshIntervalId = setInterval(() => {
          this.emit('dashboardLight', { days: this.statsRange })
        }, 30000)
      },
      loadFilters () {
        const raw = localStorage.getItem('modtools_dashboard_filters')
        if (!raw) return
        try {
          const parsed = JSON.parse(raw)
          this.activeMoney = parsed.money !== undefined ? parsed.money : this.activeMoney
          this.activeWins = parsed.wins !== undefined ? parsed.wins : this.activeWins
          this.activeLosses = parsed.losses !== undefined ? parsed.losses : this.activeLosses
        } catch (e) {
          // ignore invalid cache
        }
      },
      loadStatsRange () {
        const raw = localStorage.getItem('modtools_dashboard_stats_range')
        if (!raw) return
        const value = parseInt(raw, 10)
        if (!isNaN(value)) {
          this.statsRange = value
        }
      },
      setStatsRange (range) {
        this.statsRange = range
        localStorage.setItem('modtools_dashboard_stats_range', String(range))
        this.rangeLoading = true
        this.emit('dashboard', { days: range })
      },
      persistFilters () {
        const payload = {
          money: this.activeMoney,
          wins: this.activeWins,
          losses: this.activeLosses
        }
        localStorage.setItem('modtools_dashboard_filters', JSON.stringify(payload))
      },
      reload () {
        this.emit('reloadDashboard', { days: this.statsRange })
        this.ProgressInfo()
      },
      formatNumber (value) {
        if (value === null || value === undefined) return '—'
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      },
      formatPercent (value) {
        if (value === null || value === undefined) return '—'
        return Math.round(value)
      },
      bytesToSize (bytes) {
        if (bytes === 0 || bytes === null || bytes === undefined) return '0 B'
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
        const i = Math.floor(Math.log(bytes) / Math.log(1024))
        return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i]
      },
      formatUptime (seconds) {
        if (seconds === null || seconds === undefined) return '—'
        const secNum = Math.max(0, parseInt(seconds, 10))
        const days = Math.floor(secNum / 86400)
        const hours = Math.floor((secNum % 86400) / 3600)
        const minutes = Math.floor((secNum % 3600) / 60)
        if (days > 0) return `${days}d ${hours}h ${minutes}m`
        if (hours > 0) return `${hours}h ${minutes}m`
        return `${minutes}m`
      },
      lastCount (series) {
        if (!series || series.length === 0) return null
        return series[series.length - 1].count
      },
      filteredToplist (rows, activeServer) {
        if (!rows || rows.length === 0) return []
        return rows.filter(stat => activeServer === null || stat.server === activeServer).slice(0, 15)
      },
      latestStatus (status) {
        if (!status || status.length === 0) return null
        return status[status.length - 1]
      },
      buildOnlineChartData (history, color) {
        const safeHistory = this.trimSeries(Array.isArray(history) ? history.slice() : [])
        safeHistory.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        return {
          labels: safeHistory.map(point => this.formatChartLabel(point.date)),
          datasets: [{
            label: 'online',
            backgroundColor: color.replace('0.9', '0.12'),
            borderColor: color,
            data: safeHistory.map(point => point.peak || point.count || 0),
            meta: safeHistory.map(point => ({
              peak: point.peak || point.count || 0,
              average: point.average || point.count || 0
            })),
            fill: true,
            borderWidth: 2,
            lineTension: 0.35,
            pointRadius: 0,
            pointHitRadius: 8
          }]
        }
      },
      buildStatChartData (series) {
        const safeSeries = this.trimSeries(Array.isArray(series) ? series.slice() : [])
        safeSeries.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        return {
          labels: safeSeries.map(point => this.formatChartLabel(point.date)),
          datasets: [{
            label: 'trend',
            backgroundColor: 'rgba(0,0,0,0)',
            borderColor: 'rgba(0, 150, 136, 0.7)',
            data: safeSeries.map(point => point.count || 0),
            fill: false,
            borderWidth: 2,
            lineTension: 0.35,
            pointRadius: 0,
            pointHitRadius: 6
          }]
        }
      },
      formatChartLabel (value) {
        if (!value) return ''
        let d
        if (typeof value === 'string') {
          const dateParts = value.split('T')[0].split(' ')[0].split('-')
          if (dateParts.length === 3) {
            const y = parseInt(dateParts[0], 10)
            const m = parseInt(dateParts[1], 10) - 1
            const day = parseInt(dateParts[2], 10)
            d = new Date(Date.UTC(y, m, day))
          } else {
            d = new Date(value)
          }
        } else {
          d = new Date(value)
        }
        if (isNaN(d.getTime())) return ''
        const pad = (n) => n.toString().padStart(2, '0')
        const day = pad(d.getUTCDate())
        const month = pad(d.getUTCMonth() + 1)
        return `${day}.${month}`
      },
      refreshOnlineCharts () {
        const silver = this.onlineHistory && this.onlineHistory.silver ? this.onlineHistory.silver : []
        const gold = this.onlineHistory && this.onlineHistory.gold ? this.onlineHistory.gold : []
        this.silverChartData = this.buildOnlineChartData(silver, 'rgba(203, 211, 219, 0.9)')
        this.goldChartData = this.buildOnlineChartData(gold, 'rgba(230, 199, 146, 0.9)')
        this.$nextTick(() => {
          if (this.$refs.silverChart) this.$refs.silverChart.update()
          if (this.$refs.goldChart) this.$refs.goldChart.update()
        })
      },
      refreshStatCharts () {
        const active = this.stats && this.stats.active_players ? this.trimSeries(this.mergeServerSeries(this.stats.active_players.silver || [], this.stats.active_players.gold || [])) : []
        const newPlayers = this.getNewPlayersSeries() || []
        const avgMoney = this.stats && this.stats.average_money ? this.trimSeries(this.mergeServerSeries(this.stats.average_money.silver || [], this.stats.average_money.gold || [])) : []

        this.activePlayersChart = this.buildStatChartData(active)
        this.newPlayersChart = this.buildStatChartData(newPlayers)
        this.averageMoneyChart = this.buildStatChartData(avgMoney)
      },
      statsSignature () {
        const parts = []
        const series = [
          this.stats && this.stats.active_players,
          this.stats && this.stats.new_players,
          this.stats && this.stats.average_money
        ]
        for (let i = 0; i < series.length; i++) {
          const map = series[i] || {}
          const keys = Object.keys(map).sort()
          for (let k = 0; k < keys.length; k++) {
            const list = Array.isArray(map[keys[k]]) ? map[keys[k]] : []
            if (list.length === 0) {
              parts.push(`${i}:${keys[k]}:0`)
              continue
            }
            const sorted = list.slice().sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            const last = sorted[sorted.length - 1]
            parts.push(`${i}:${keys[k]}:${sorted.length}:${last ? last.date : ''}:${last ? last.count : ''}`)
          }
        }
        parts.push(`range:${this.statsRange}`)
        return parts.join('|')
      },
      onlineHistorySignature (history) {
        if (!history) return ''
        const parts = []
        const keys = Object.keys(history).sort()
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i]
          const series = Array.isArray(history[key]) ? history[key] : []
          if (series.length === 0) {
            parts.push(`${key}:0`)
            continue
          }
          const sorted = series.slice().sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
          const last = sorted[sorted.length - 1]
          parts.push(`${key}:${sorted.length}:${last ? last.date : ''}:${last ? last.count : ''}`)
        }
        return parts.join('|')
      },
      mergeServerSeries (silver, gold) {
        const out = []
        const max = Math.max(silver.length, gold.length)
        for (let i = 0; i < max; i++) {
          const s = silver[i]
          const g = gold[i]
          const date = (s && s.date) || (g && g.date)
          const count = (s ? s.count : 0) + (g ? g.count : 0)
          if (date) {
            out.push({date, count})
          }
        }
        return out
      },
      getNewPlayersSeries () {
        if (!this.stats || !this.stats.new_players) return null
        if (this.stats.new_players.gold && this.stats.new_players.gold.length > 0) {
          return this.trimSeries(this.stats.new_players.gold)
        }
        if (this.stats.new_players.silver && this.stats.new_players.silver.length > 0) {
          return this.trimSeries(this.stats.new_players.silver)
        }
        return null
      },
      trimSeries (series) {
        const safeSeries = Array.isArray(series) ? series : []
        const days = Math.max(1, this.statsRange || 1)
        if (safeSeries.length <= days) return safeSeries
        return safeSeries.slice(-days)
      },
      serverAccentClass (server) {
        if (!server) return ''
        const key = server.toLowerCase()
        if (key === 'silver') return 'accent-silver'
        if (key === 'gold') return 'accent-gold'
        return ''
      },
      formatAge (value) {
        if (!value) return '—'
        const date = new Date(value)
        if (isNaN(date.getTime())) return '—'
        const diff = Math.max(0, Date.now() - date.getTime())
        const seconds = Math.floor(diff / 1000)
        if (seconds < 60) return `${seconds}s ago`
        const minutes = Math.floor(seconds / 60)
        if (minutes < 60) return `${minutes}m ago`
        const hours = Math.floor(minutes / 60)
        if (hours < 24) return `${hours}h ago`
        const days = Math.floor(hours / 24)
        return `${days}d ago`
      },
      detectAnomaly (series) {
        if (!series || series.length < 6) return ''
        const last = series[series.length - 1]
        if (!last) return ''
        const base = series.slice(-7, -1)
        const avg = base.reduce((sum, row) => sum + (row.count || 0), 0) / base.length
        if (avg <= 0) return ''
        const ratio = (last.count || 0) / avg
        if (ratio >= 1.6) return 'Spike'
        if (ratio <= 0.6) return 'Drop'
        return ''
      },
      confirmRestart (server) {
        let self = this
        let serverName = server === 'gold' ? 'Gold' : 'Silver'
        iziToast.question({
          timeout: 20000,
          close: true,
          overlay: true,
          toastOnce: true,
          id: 'question',
          zindex: 99999,
          message: 'Are you sure that you want to restart <strong>' + serverName + '</strong>?',
          position: 'center',
          buttons: [
            ['<button><b>YES</b></button>', function (instance, toast) {
              iziToast.hide({}, toast)
              self.emit('restartServer', {
                server: server
              })
            }, true]
          ]
        })
      }
    },
    notifications: {
      ProgressInfo: {
        type: VueNotifications.types.info,
        title: 'Reload in progress!',
        message: 'This may take several seconds, please be patient.'
      }
    },
    computed: {
      ...mapGetters({
        stats: 'stats',
        restartError: 'RestartError',
        statusSilver: 'statusSilver',
        statusGold: 'statusGold',
        serverStats: 'serverStats',
        onlineHistory: 'onlineHistory',
        dashboardGeneratedAt: 'dashboardGeneratedAt',
        statsUpdatedAt: 'dashboardStatsUpdatedAt',
        dashboardMaxStatsDays: 'dashboardMaxStatsDays'
      }),
      serverStatsList () {
        return []
      },
      silverStatus () {
        return this.latestStatus(this.statusSilver)
      },
      goldStatus () {
        return this.latestStatus(this.statusGold)
      },
      silverCount () {
        return this.silverStatus ? this.silverStatus.player_count : null
      },
      goldCount () {
        return this.goldStatus ? this.goldStatus.player_count : null
      },
      silverOnline () {
        return this.silverStatus ? this.silverStatus.online : false
      },
      goldOnline () {
        return this.goldStatus ? this.goldStatus.online : false
      },
      availableRanges () {
        const maxDays = this.dashboardMaxStatsDays || 14
        const ranges = [
          { label: '7d', days: 7 },
          { label: '3m', days: 90 },
          { label: '6m', days: 180 },
          { label: '1y', days: 365 }
        ]
        return ranges.filter(range => range.days <= maxDays)
      },
      trimmedActivePlayers () {
        if (!this.stats || !this.stats.active_players) return { silver: [], gold: [] }
        return {
          silver: this.trimSeries(this.stats.active_players.silver || []),
          gold: this.trimSeries(this.stats.active_players.gold || [])
        }
      },
      trimmedNewPlayers () {
        if (!this.stats || !this.stats.new_players) return { silver: [], gold: [] }
        return {
          silver: this.trimSeries(this.stats.new_players.silver || []),
          gold: this.trimSeries(this.stats.new_players.gold || [])
        }
      },
      trimmedAverageMoney () {
        if (!this.stats || !this.stats.average_money) return { silver: [], gold: [] }
        return {
          silver: this.trimSeries(this.stats.average_money.silver || []),
          gold: this.trimSeries(this.stats.average_money.gold || [])
        }
      },
      activePlayersSilver () {
        return this.stats ? this.lastCount(this.trimmedActivePlayers.silver) : null
      },
      activePlayersGold () {
        return this.stats ? this.lastCount(this.trimmedActivePlayers.gold) : null
      },
      activePlayersTotal () {
        let total = 0
        let hasAny = false
        let silver = this.activePlayersSilver
        let gold = this.activePlayersGold
        if (silver !== null && silver !== undefined) {
          total += silver
          hasAny = true
        }
        if (gold !== null && gold !== undefined) {
          total += gold
          hasAny = true
        }
        return hasAny ? total : null
      },
      newPlayersSilver () {
        return this.stats ? this.lastCount(this.trimmedNewPlayers.silver) : null
      },
      newPlayersGold () {
        return this.stats ? this.lastCount(this.trimmedNewPlayers.gold) : null
      },
      newPlayersTotal () {
        const series = this.getNewPlayersSeries()
        return series ? this.lastCount(series) : null
      },
      averageMoneySilver () {
        return this.stats ? this.lastCount(this.trimmedAverageMoney.silver) : null
      },
      averageMoneyGold () {
        return this.stats ? this.lastCount(this.trimmedAverageMoney.gold) : null
      },
      averageMoneyTotal () {
        let values = []
        if (this.averageMoneySilver !== null && this.averageMoneySilver !== undefined) {
          values.push(this.averageMoneySilver)
        }
        if (this.averageMoneyGold !== null && this.averageMoneyGold !== undefined) {
          values.push(this.averageMoneyGold)
        }
        if (values.length === 0) return null
        let total = values.reduce((sum, val) => sum + val, 0)
        return Math.round(total / values.length)
      },
      activePlayersChartData () {
        return this.activePlayersChart
      },
      newPlayersChartData () {
        return this.newPlayersChart
      },
      averageMoneyChartData () {
        return this.averageMoneyChart
      },
      statsLoading () {
        return this.stats === null
      },
      statusLoading () {
        return !this.dashboardGeneratedAt && (!this.statusSilver || this.statusSilver.length === 0)
      },
      isLoading () {
        return this.stats === null || this.onlineHistory === null
      },
      silverAnomalyLabel () {
        const history = this.onlineHistory && this.onlineHistory.silver ? this.onlineHistory.silver : []
        return this.detectAnomaly(history)
      },
      goldAnomalyLabel () {
        const history = this.onlineHistory && this.onlineHistory.gold ? this.onlineHistory.gold : []
        return this.detectAnomaly(history)
      },
    },
    watch: {
      activeMoney () {
        this.persistFilters()
      },
      activeWins () {
        this.persistFilters()
      },
      activeLosses () {
        this.persistFilters()
      },
      onlineHistory: function () {
        const sig = this.onlineHistorySignature(this.onlineHistory)
        if (sig === this.lastOnlineHistorySig) return
        this.lastOnlineHistorySig = sig
        this.refreshOnlineCharts()
      },
      stats: function () {
        const sig = this.statsSignature()
        if (sig === this.lastStatsSig) return
        this.lastStatsSig = sig
        this.refreshStatCharts()
        if (this.rangeLoading) this.rangeLoading = false
      },
      statsRange: function () {
        const sig = this.statsSignature()
        if (sig === this.lastStatsSig) return
        this.lastStatsSig = sig
        this.refreshStatCharts()
        if (this.rangeLoading) this.rangeLoading = false
      },
      restartError: function (e) {
        if (e.error) {
          iziToast.error({title: 'Server restart failed!', message: e.error, tiomeout: 3000, transitionIn: 'bounceInLeft'})
        } else {
          iziToast.success({title: 'Server restart!', message: 'Server restart request was sent, server should be up within the next seconds.', tiomeout: 3000, transitionIn: 'bounceInLeft'})
        }
      }
    }
  }
</script>

<style scoped>
.dashboard-modern {
  --accent-silver: #cbd3db;
  --accent-gold: #e6c792;
  --accent-primary: #009688;
  --accent-primary-soft: rgba(0, 150, 136, 0.16);
  --accent-ink: #0c1a24;
  --accent-glow: rgba(215, 222, 230, 0.08);
  --card-border: #23282c;
  --card-surface: #3a4148;
  --card-cap: #343b41;
  --list-hover: #2f343a;
}

.dashboard-modern .card {
  border-radius: 2px;
  border: 1px solid var(--card-border);
  background: var(--card-surface);
  position: relative;
}

.dashboard-loading-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px 12px;
  color: #b8c6cf;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
}

.dashboard-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 6px 12px;
  margin-bottom: 12px;
  background: rgba(15, 22, 28, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.meta-block {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  color: #b8c6cf;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.meta-label {
  color: #7f9199;
}

.meta-value {
  color: #d4e2e8;
  font-weight: 700;
}

.meta-warning {
  color: #f6c343;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.meta-help {
  display: inline-flex;
  align-items: center;
  color: #7f9199;
  font-size: 0.8rem;
  cursor: help;
}

.meta-help:hover {
  color: #cbd3db;
}

.meta-block--range {
  margin-left: auto;
}

.range-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #2a343b;
  padding: 3px;
  border-radius: 6px;
  border: 1px solid #324047;
}

.range-pill {
  border: none;
  background: transparent;
  color: #9eb1ba;
  font-size: 0.7rem;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.15s ease;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.range-pill.active {
  background: #324047;
  color: #e6f0f2;
  box-shadow: inset 0 0 0 1px rgba(42, 163, 169, 0.35);
}

.status-card {
  position: relative;
  overflow: visible;
  min-height: 156px;
}

.status-card::before {
  content: '';
  position: absolute;
  inset: -40% 0 auto 0;
  height: 140%;
  background: transparent;
  pointer-events: none;
  opacity: 0.25;
}

.status-card::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, rgba(215, 222, 230, 0.5), transparent);
  opacity: 0.7;
}

.status-card--silver {
  border-color: var(--card-border);
  box-shadow: none;
}

.status-card--gold {
  border-color: var(--card-border);
  box-shadow: none;
}

.status-card--silver::after {
  background: linear-gradient(180deg, rgba(203, 211, 219, 0.55), rgba(203, 211, 219, 0.18), transparent);
}

.status-card--gold::after {
  background: linear-gradient(180deg, rgba(230, 199, 146, 0.6), rgba(230, 199, 146, 0.2), transparent);
}

.status-card--silver .status-value {
  color: var(--accent-silver);
  text-shadow: none;
}

.status-card--gold .status-value {
  color: var(--accent-gold);
  text-shadow: none;
}

.status-chart {
  padding: 0 12px 10px;
  position: relative;
}

.mini-chart {
  margin-top: 8px;
  position: relative;
}

.status-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
  color: #8fa3ad;
}

.status-value {
  font-size: 2rem;
  font-weight: 700;
}


.stat-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
  color: #8fa3ad;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
}

.stat-card {
  background: var(--card-surface);
  border-color: var(--card-border);
  box-shadow: none;
  min-height: 168px;
}

.stat-card::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, rgba(0, 150, 136, 0.35), transparent 70%);
}

.stat-card:nth-of-type(1) .stat-value {
  color: #e6eaee;
}

.stat-card:nth-of-type(2) .stat-value {
  color: #e6eaee;
}

.stat-card:nth-of-type(3) .stat-value {
  color: #e6eaee;
}

.stat-card:nth-of-type(1)::after {
  background: linear-gradient(90deg, rgba(0, 150, 136, 0.35), transparent 70%);
}

.stat-card:nth-of-type(2)::after {
  background: linear-gradient(90deg, rgba(0, 150, 136, 0.35), transparent 70%);
}

.stat-card:nth-of-type(3)::after {
  background: linear-gradient(90deg, rgba(0, 150, 136, 0.35), transparent 70%);
}

.list-card .list-group-item:hover {
  background: var(--list-hover);
  border-left-color: var(--accent-primary);
}

.list-card .list-group-item {
  padding: 0.6rem 0.9rem;
  border-left: 2px solid transparent;
  background: var(--card-cap);
  border-color: var(--card-border);
}

.list-card .list-group-item.accent-silver {
  border-left-color: rgba(203, 211, 219, 0.5);
}

.list-card .list-group-item.accent-gold {
  border-left-color: rgba(230, 199, 146, 0.55);
}

.list-card .list-group-item:first-child {
  border-top-color: var(--card-border);
}

.list-card .list-group-item:last-child {
  border-bottom-color: var(--card-border);
}

.list-card .card-header,
.list-card .card-header:first-child {
  background: var(--card-cap);
  border-bottom: 1px solid var(--card-border);
}

.dashboard-modern .img-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
}

.stat-value,
.status-value {
  color: inherit;
}

.toplist-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}

.toplist-name a {
  color: #e6f0f2;
  font-weight: 600;
}

.toplist-meta {
  font-size: 0.72rem;
}

.toplist-value {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 96px;
  text-align: right;
  margin-left: auto;
}

.value-label {
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.value-badge {
  display: inline-block;
  margin-top: 2px;
  font-weight: 600;
  font-size: 0.76rem;
  border-color: rgba(0, 150, 136, 0.22);
}

.skeleton-text {
  display: inline-block;
  height: 0.7rem;
  width: 110px;
  border-radius: 3px;
  background: linear-gradient(90deg, rgba(60, 70, 78, 0.35), rgba(90, 104, 114, 0.55), rgba(60, 70, 78, 0.35));
  background-size: 200% 100%;
  animation: shimmer 1.6s ease infinite;
}

.skeleton-text--lg {
  height: 1.2rem;
  width: 140px;
}

.skeleton-text--sm {
  height: 0.55rem;
  width: 80px;
}

.skeleton-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(90deg, rgba(60, 70, 78, 0.35), rgba(90, 104, 114, 0.55), rgba(60, 70, 78, 0.35));
  background-size: 200% 100%;
  animation: shimmer 1.6s ease infinite;
}

 .chart-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(12, 26, 36, 0.45);
  color: #8fd6cc;
  font-size: 1.1rem;
  z-index: 2;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

</style>
